/**
 * Shared color utility functions for branding and PDF export.
 */

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const num = parseInt(hex.replace('#', ''), 16)
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff,
  }
}

export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.min(255, (num >> 16) + amt)
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt)
  const B = Math.min(255, (num & 0x0000ff) + amt)
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
}

export function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.max(0, (num >> 16) - amt)
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt)
  const B = Math.max(0, (num & 0x0000ff) - amt)
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
}

/**
 * WCAG relative luminance contrast check.
 * Returns '#FFFFFF' for dark backgrounds, '#1F2937' for light backgrounds.
 */
export function getContrastTextColor(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  // sRGB to linear
  const toLinear = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  return luminance > 0.179 ? '#1F2937' : '#FFFFFF'
}

/**
 * Fetch an image URL and return it as a base64 data-URL via an offscreen canvas.
 * Returns null on any failure (network error, CORS, invalid image).
 */
export async function fetchImageAsBase64(url: string): Promise<string | null> {
  // Try fetch() first — avoids CORS tainted canvas issues
  try {
    const response = await fetch(url, { credentials: 'include' })
    if (!response.ok) throw new Error('Fetch failed')
    const blob = await response.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    // Fallback: try Image element (works for data URIs and same-origin)
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      const loaded = await new Promise<HTMLImageElement>((resolve, reject) => {
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = url
      })

      const canvas = document.createElement('canvas')
      canvas.width = loaded.naturalWidth
      canvas.height = loaded.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      ctx.drawImage(loaded, 0, 0)
      return canvas.toDataURL('image/png')
    } catch {
      return null
    }
  }
}
